import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { Delete, Visibility } from "@mui/icons-material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import { FaLightbulb, FaCogs, FaChartBar } from "react-icons/fa";
import { styled } from "@mui/material/styles";
import axios from "axios";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: "40px",
  borderRadius: "24px",
  boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.15)",
  background: "linear-gradient(135deg, #f0f5ff, #ffffff)",
  position: "relative",
  overflow: "hidden",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#6C63FF",
  color: "#fff",
  fontWeight: "bold",
  padding: "12px 24px",
  borderRadius: "32px",
  textTransform: "none",
  fontSize: "16px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#574fdc",
    boxShadow: "0px 10px 15px rgba(92, 84, 216, 0.3)",
    transform: "translateY(-2px)",
  },
}));

function IdeaSubmissionForm() {
  const [formData, setFormData] = useState({
    stud_id: "",
    title: "",
    description: "",
    videos: [],
  });

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    const authToken = localStorage.getItem("authToken");

    if (userId && authToken) {
      setFormData((prev) => ({ ...prev, stud_id: userId }));
      axios.defaults.headers.common["Authorization"] = authToken;
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const updatedFiles = acceptedFiles.map((file) => ({
        file,
        type: file.type,
      }));
      setFormData((prev) => ({
        ...prev,
        videos: [...prev.videos, ...updatedFiles],
      }));
    },
    accept: "video/*",
  });

  const handleRemoveFile = (fileToRemove) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos.filter((file) => file.file !== fileToRemove),
    }));
  };

  const handlePreview = (file) => {
    Swal.fire({
      html: `<video controls style="width: 100%;"><source src="${URL.createObjectURL(
        file.file
      )}" type="${file.type}"></video>`,
      width: "800px",
      showConfirmButton: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      payload.append("stud_id", formData.stud_id);
      payload.append("title", formData.title);
      payload.append("description", formData.description);

      formData.videos.forEach(({ file }) => {
        payload.append("idea", file);
      });

      const response = await axios.post(
        "https://puneexpobackend.tdtlworld.com/api/idea_submission/",
        payload
      );

      if (response.data.msg === "Idea submitted successfully.") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.msg,
          confirmButtonColor: "#4caf50",
        });
        setFormData({
          stud_id: formData.stud_id,
          title: "",
          description: "",
          videos: [],
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.msg || "Submission failed.",
        confirmButtonColor: "#f44336",
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
      }}
    >
      <StyledPaper>
        <Typography
          variant="h4"
          sx={{
            mb: 5,
            textAlign: "center",
            fontWeight: "bold",
            color: "#6C63FF",
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Submit Your Innovation Idea
        </Typography>
        <form onSubmit={handleSubmit}>
          <Timeline position="alternate" sx={{ padding: 0 }}>
            {/* Discovery Section */}
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot sx={{ bgcolor: "#ff7043" }}>
                  <FaLightbulb style={{ color: "#fff", fontSize: "1.5rem" }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, color: "#333" }}
                  >
                    Discovery
                  </Typography>
                  <TextField
                    fullWidth
                    label="Project Title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Box>
              </TimelineContent>
            </TimelineItem>

            {/* Ideation Section */}
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot sx={{ bgcolor: "#42a5f5" }}>
                  <FaChartBar style={{ color: "#fff", fontSize: "1.5rem" }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, color: "#333" }}
                  >
                    Ideation
                  </Typography>
                  <TextField
                    fullWidth
                    label="Idea Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    multiline
                    rows={4}
                    variant="outlined"
                  />
                </Box>
              </TimelineContent>
            </TimelineItem>

            {/* Execution Section */}
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot sx={{ bgcolor: "#66bb6a" }}>
                  <FaCogs style={{ color: "#fff", fontSize: "1.5rem" }} />
                </TimelineDot>
              </TimelineSeparator>
              <TimelineContent>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", mb: 2, color: "#333" }}
                  >
                    Execution
                  </Typography>
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "2px dashed #6C63FF",
                      padding: 4,
                      textAlign: "center",
                      cursor: "pointer",
                      borderRadius: 2,
                      bgcolor: "#f5f5f5",
                      "&:hover": { bgcolor: "#e3e3e3" },
                      mb: 2,
                    }}
                  >
                    <input {...getInputProps()} />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "#6C63FF" }}
                    >
                      Drag and drop your video here, or click to select
                    </Typography>
                  </Box>
                  <List>
                    {formData.videos.map((file, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={file.file.name} />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handlePreview(file)}
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveFile(file.file)}
                          >
                            <Delete />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </TimelineContent>
            </TimelineItem>
          </Timeline>

          {/* Submit Button */}
          <StyledButton type="submit" fullWidth>
            Submit Your Idea
          </StyledButton>
        </form>
      </StyledPaper>
    </Box>
  );
}

export default IdeaSubmissionForm;
